"use client";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Select from "react-select";

// ─────────────────────────────────────────────────────────────────────
// SEARCH UTILITIES (ported from searchUtils.js for budget context)
// ─────────────────────────────────────────────────────────────────────
function normalise(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/[''"""]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildItemSearchBlob(item) {
  const parts = [
    item.conditionName,
    item.conditionNo || item.takeoffConditionNo
      ? `#${item.conditionNo || item.takeoffConditionNo}`
      : "",
    item.sectionNumber,
    item.category,
    item.itemDescription,
    item.costCodeDescription,
    item.size,
    item.latestSize,
    item.uom,
    item.container,
    item.bidArea,
    item.materialCode,
    item.laborCode,
    item.note,
    // Vendor name if populated
    typeof item.vendor === "object"
      ? item.vendor?.companyName || ""
      : item._vendorName || "",
  ];
  return normalise(parts.join("  "));
}

function tokenVariants(token) {
  const variants = [token];

  // "358" → "3-5/8", "3 5/8", "35/8"
  const fractionCondensed = token.match(/^(\d+)(\d)(\d)$/);
  if (fractionCondensed) {
    const [, a, b, c] = fractionCondensed;
    variants.push(`${a}-${b}/${c}`);
    variants.push(`${a} ${b}/${c}`);
    variants.push(`${a}${b}/${c}`);
  }

  // "3-5/8" → "358"
  const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d)$/);
  if (fractionExpanded) {
    const [, a, b, c] = fractionExpanded;
    variants.push(`${a}${b}${c}`);
  }

  // "20ga" → "20ga", "20 ga", "20gauge"
  const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
  if (gaugeMatch) {
    const [, num] = gaugeMatch;
    variants.push(`${num}ga`);
    variants.push(`${num} ga`);
    variants.push(`${num}gauge`);
  }

  // "drywall" ↔ "dry wall"
  if (token === "drywall") variants.push("dry wall");
  if (token === "dry") variants.push("drywall");

  return variants;
}

function multiTokenSearchItems(items, searchStr) {
  const raw = normalise(searchStr);
  if (!raw) return items;

  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return items;

  return items.filter((item) => {
    const blob = item._searchBlob || buildItemSearchBlob(item);
    if (!item._searchBlob) item._searchBlob = blob;

    return tokens.every((token) => {
      const variants = tokenVariants(token);
      return variants.some((v) => blob.includes(v));
    });
  });
}

// ─────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────
const normalizeConditionName = (conditionName) => {
  if (!conditionName) return conditionName;
  const str = String(conditionName).trim();
  const match = str.match(/^\d+\s*[-–—]\s*(.+)$/);
  return match ? match[1].trim() : str;
};

function getSafeCoverage(coverage) {
  if (
    coverage === null ||
    coverage === undefined ||
    coverage === "" ||
    coverage === "-" ||
    String(coverage).trim().toUpperCase() === "N/A" ||
    String(coverage).trim() === "NONE"
  )
    return 1;
  const num = Number(coverage);
  return isNaN(num) || num <= 0 ? 1 : num;
}

const formatNumber = (num) =>
  (num ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getDisplayQty = (item) => {
  if (!item) return 0;
  if (item.netRequiredQty !== undefined) return item.netRequiredQty;
  return item.category === "L" ? (item.quantity ?? 0) : (item.requiredQty ?? 0);
};

const getDisplayPrice = (item) => {
  if (!item) return 0;
  if (item.latestPrice !== undefined) return item.latestPrice;
  if (item.category === "L") return item.unitRate ?? item.price ?? 0;
  return item.price ?? item.cost ?? 0;
};

const getDisplayUom = (item) => {
  if (!item || item.category !== "M")
    return item?.uom || item?.container || "—";
  const uom = (item.uom || item.container || "").trim().toUpperCase();
  return ["SF", "LF"].some((u) => uom.includes(u)) ? "PC" : uom || "—";
};

const getDisplayPackageCost = (item) => {
  if (!item || item.category !== "M") return getDisplayPrice(item);
  const uom = (item.uom || item.container || "").trim().toUpperCase();
  const isCoverageBased = ["SF", "LF"].some((u) => uom.includes(u));
  if (!isCoverageBased) return getDisplayPrice(item);
  const coverage = Number(parseFloat(item.coveragePerUnit)) || 1;
  return getDisplayPrice(item) * coverage;
};

// ─────────────────────────────────────────────────────────────────────
// CUSTOM STYLES for react-select (matching existing ERP theme)
// ─────────────────────────────────────────────────────────────────────
const multiSelectFilterStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    minHeight: "36px",
    fontSize: "13px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "13px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#374151",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
    border: "1px solid #4b5563",
    width: "280px",
    maxWidth: "320px",
    zIndex: 999999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "320px",
    padding: "6px",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: "12.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.35",
    "&:hover": { backgroundColor: "#4b5563" },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 6px",
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4b5563",
    borderRadius: "4px",
    margin: 0,
    fontSize: "12px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
    padding: "2px 6px",
    maxWidth: "130px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9ca3af",
    cursor: "pointer",
    ":hover": { backgroundColor: "#f97316", color: "white" },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12.5px",
  }),
};

// ─────────────────────────────────────────────────────────────────────
// STATUS BADGES (matching BudgetTab)
// ─────────────────────────────────────────────────────────────────────
const ItemStatusBadges = ({ item }) => {
  const badges = [];

  if (item.isHistoricalPriceLine) {
    badges.push(
      <span
        key="historical"
        className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-gray-600/80 text-gray-200 border border-gray-500/50"
      >
        ORDERED @ OLD PRICE
      </span>,
    );
  } else if (item.isPriceChangeRow) {
    badges.push(
      <span
        key="newprice"
        className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-purple-600/80 text-purple-100 border border-purple-400/50"
      >
        NEW PRICE
      </span>,
    );
  }

  if (item.isNewSpec) {
    badges.push(
      <span
        key="sizechange"
        className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-blue-600/80 text-blue-100 border border-blue-400/50"
      >
        SIZE CHANGE
      </span>,
    );
  }

  if (item.closedOutBySizeChange) {
    badges.push(
      <span
        key="closed"
        className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-yellow-600/80 text-yellow-100 border border-yellow-400/50"
      >
        CLOSED
      </span>,
    );
  }

  if (
    item.hasVariance &&
    !item.isHistoricalPriceLine &&
    !item.isPriceChangeRow &&
    !item.isNewSpec
  ) {
    badges.push(
      <span
        key="variance"
        className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-amber-600/80 text-amber-100 border border-amber-400/50"
      >
        VARIANCE
      </span>,
    );
  }

  if (badges.length === 0) return null;
  return <div className="flex gap-1 flex-wrap mt-0.5">{badges}</div>;
};

// ─────────────────────────────────────────────────────────────────────
// SOURCE BADGE
// ─────────────────────────────────────────────────────────────────────
const SourceBadge = ({ item }) => {
  const sourceType = item._sourceType || item.sourceType;
  if (sourceType === "change_order" || item.isChangeOrderItem) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-purple-600/70 text-purple-100">
        CO
      </span>
    );
  }
  if (sourceType === "variance" || item.isVarianceDelta || item.isVariance) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-amber-600/70 text-amber-100">
        VAR
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-gray-600/70 text-gray-300">
      BASE
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
export default function AssemblyBudgetView({
  allItems,
  loading,
  vendors,
  project,
  selectedLineItems,
  onSelectLineItem,
  onSelectAllFiltered,
  getItemSelectionKey,
  onCreateVariances,
}) {
  // ── State ──
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  const [expandedAssemblies, setExpandedAssemblies] = useState(new Set());
  const [expandedItemId, setExpandedItemId] = useState(null);

  // Top-level bid area filter (filters assemblies before grouping)
  const [bidAreaFilter, setBidAreaFilter] = useState([]);

  // Column filters
  const [filters, setFilters] = useState({
    category: [],
    section: [],
    bidArea: [],
    vendor: [],
    uom: [],
    size: [],
  });

  const [showFilter, setShowFilter] = useState(null);

  // Pagination per assembly
  const ITEMS_PER_ASSEMBLY_PAGE = 200;
  const [assemblyPages, setAssemblyPages] = useState({});

  // Refs for click-outside
  const filterRefs = {
    category: useRef(null),
    section: useRef(null),
    bidArea: useRef(null),
    vendor: useRef(null),
    uom: useRef(null),
    size: useRef(null),
  };

  // ── Click outside handler ──
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showFilter) return;
      const activeRef = filterRefs[showFilter];
      if (activeRef?.current && !activeRef.current.contains(event.target)) {
        const isInsidePortal =
          event.target.closest('[class*="-menu"]') ||
          event.target.closest('[id^="react-select"]');
        if (!isInsidePortal) setShowFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilter]);

  // ── Debounced search ──
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 250);
  }, []);

  // ── Vendor lookup ──
  const vendorMap = useMemo(() => {
    const map = {};
    (vendors || []).forEach((v) => {
      map[v._id] = v.companyName;
    });
    return map;
  }, [vendors]);

  // ── Enrich items with vendor names for search ──
  const enrichedItems = useMemo(() => {
    return (allItems || []).map((item) => {
      const vendorId =
        item.vendor && typeof item.vendor === "object"
          ? item.vendor._id
          : typeof item.vendor === "string"
            ? item.vendor
            : null;
      return {
        ...item,
        _vendorName: vendorId ? vendorMap[vendorId] || "" : "",
        _vendorId: vendorId,
        _normalizedCondition: normalizeConditionName(item.conditionName),
      };
    });
  }, [allItems, vendorMap]);

  // ── Top-level bid area options (computed from all items) ──
  const bidAreaOptions = useMemo(() => {
    const areas = new Set();
    enrichedItems.forEach((item) => {
      if (item.bidArea) areas.add(item.bidArea);
    });
    return Array.from(areas)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [enrichedItems]);

  // ── Apply top-level bid area filter ──
  const bidAreaFilteredItems = useMemo(() => {
    if (bidAreaFilter.length === 0) return enrichedItems;
    return enrichedItems.filter(
      (item) => item.bidArea && bidAreaFilter.includes(item.bidArea),
    );
  }, [enrichedItems, bidAreaFilter]);

  // ── Apply column filters ──
  const columnFilteredItems = useMemo(() => {
    return bidAreaFilteredItems.filter((item) => {
      if (
        filters.category.length > 0 &&
        !filters.category.includes(item.category)
      )
        return false;
      if (
        filters.section.length > 0 &&
        !filters.section.includes(item.sectionNumber)
      )
        return false;
      if (filters.bidArea.length > 0 && !filters.bidArea.includes(item.bidArea))
        return false;
      if (filters.uom.length > 0) {
        const uom = item.uom || item.container || "";
        if (!filters.uom.includes(uom)) return false;
      }
      if (filters.size.length > 0 && !filters.size.includes(item.size))
        return false;
      if (filters.vendor.length > 0) {
        const hasNullFilter = filters.vendor.includes(null);
        const hasVendorMatch = filters.vendor.includes(item._vendorId);
        if (
          item.category !== "M" ||
          (!hasVendorMatch && !(hasNullFilter && !item._vendorId))
        )
          return false;
      }
      return true;
    });
  }, [bidAreaFilteredItems, filters]);

  // ── Apply multi-token search ──
  const filteredItems = useMemo(() => {
    if (!debouncedSearch) return columnFilteredItems;
    return multiTokenSearchItems(columnFilteredItems, debouncedSearch);
  }, [columnFilteredItems, debouncedSearch]);

  // ── Group by normalized assembly (conditionName) ──
  const assemblyGroups = useMemo(() => {
    const groups = new Map();

    filteredItems.forEach((item) => {
      const key = item._normalizedCondition || "Uncategorized";
      if (!groups.has(key)) {
        groups.set(key, {
          assemblyName: key,
          rawConditionName: item.conditionName || "Uncategorized",
          // the takeoff assembly's own number (#7), when the row came from a published takeoff
          assemblyNo:
            Number(item.conditionNo || item.takeoffConditionNo) || null,
          items: [],
          totalAmount: 0,
          materialAmount: 0,
          laborAmount: 0,
          itemCount: 0,
          hasVariance: false,
          hasChangeOrder: false,
          // CO/Variance breakdown totals
          coTotalDeltaQty: 0,
          coTotalDeltaAmount: 0,
          varianceTotalDeltaQty: 0,
          varianceTotalDeltaAmount: 0,
          coBreakdownCount: 0,
          varianceBreakdownCount: 0,
        });
      }
      const group = groups.get(key);
      if (!group.assemblyNo) {
        const no = Number(item.conditionNo || item.takeoffConditionNo);
        if (no > 0) group.assemblyNo = no;
      }
      group.items.push(item);
      group.itemCount++;

      if (!item.isHistoricalPriceLine) {
        group.totalAmount += item.amount || 0;
        if (item.category === "M") group.materialAmount += item.amount || 0;
        if (item.category === "L") group.laborAmount += item.amount || 0;
      }

      if (item.hasVariance || item.isVarianceDelta || item.isVariance)
        group.hasVariance = true;
      if (item.isChangeOrderItem || item._sourceType === "change_order")
        group.hasChangeOrder = true;

      // Aggregate CO/Variance breakdown data from changeOrderBreakdown
      const breakdowns =
        item.changeOrderBreakdown || item.varianceBreakdown || [];
      breakdowns.forEach((bd) => {
        if (bd.budgetType === "change_order") {
          group.coTotalDeltaQty += bd.deltaQty || 0;
          group.coTotalDeltaAmount += bd.deltaAmount || 0;
          group.coBreakdownCount++;
        } else {
          group.varianceTotalDeltaQty += bd.deltaQty || 0;
          group.varianceTotalDeltaAmount += bd.deltaAmount || 0;
          group.varianceBreakdownCount++;
        }
      });
    });

    // Sort items within each group
    groups.forEach((group) => {
      group.items.sort((a, b) => {
        const secA = a.sectionNumber || "";
        const secB = b.sectionNumber || "";
        if (secA !== secB) return secA.localeCompare(secB);
        if (a.isHistoricalPriceLine && !b.isHistoricalPriceLine) return -1;
        if (!a.isHistoricalPriceLine && b.isHistoricalPriceLine) return 1;
        return (a.itemNumber || 0) - (b.itemNumber || 0);
      });
    });

    // Sort groups by assembly number (numbered ones first), then alphabetically
    const sorted = Array.from(groups.values()).sort(
      (a, b) =>
        (a.assemblyNo || Infinity) - (b.assemblyNo || Infinity) ||
        a.assemblyName.localeCompare(b.assemblyName),
    );

    return sorted;
  }, [filteredItems]);

  // ── Grand totals ──
  const grandTotals = useMemo(() => {
    return assemblyGroups.reduce(
      (acc, g) => ({
        total: acc.total + g.totalAmount,
        materials: acc.materials + g.materialAmount,
        labor: acc.labor + g.laborAmount,
        items: acc.items + g.itemCount,
        assemblies: acc.assemblies + 1,
      }),
      { total: 0, materials: 0, labor: 0, items: 0, assemblies: 0 },
    );
  }, [assemblyGroups]);

  // ── Filter options builders ──
  const getFilterOptions = useCallback(
    (field) => {
      const uniqueValues = new Set();
      bidAreaFilteredItems.forEach((item) => {
        let value;
        switch (field) {
          case "category":
            value = item.category;
            break;
          case "section":
            value = item.sectionNumber;
            break;
          case "bidArea":
            value = item.bidArea;
            break;
          case "uom":
            value =
              item.category === "M"
                ? getDisplayUom(item)
                : item.uom || item.container || "—";
            break;
          case "size":
            value = item.size;
            break;
          case "vendor":
            if (item.category === "M" && item._vendorId) value = item._vendorId;
            break;
          default:
            value = item[field];
        }
        if (value) uniqueValues.add(value);
      });

      if (field === "vendor") {
        return [
          { value: null, label: "Unassigned" },
          ...Array.from(uniqueValues).map((id) => ({
            value: id,
            label: vendorMap[id] || "Unknown",
          })),
        ];
      }

      return Array.from(uniqueValues)
        .sort()
        .map((v) => ({ value: v, label: v || "N/A" }));
    },
    [bidAreaFilteredItems, vendorMap],
  );

  // ── Toggle assembly expand/collapse ──
  const toggleAssembly = useCallback((assemblyName) => {
    setExpandedAssemblies((prev) => {
      const next = new Set(prev);
      if (next.has(assemblyName)) next.delete(assemblyName);
      else next.add(assemblyName);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedAssemblies(new Set(assemblyGroups.map((g) => g.assemblyName)));
  }, [assemblyGroups]);

  const collapseAll = useCallback(() => {
    setExpandedAssemblies(new Set());
  }, []);

  // ── Filter handlers ──
  const handleFilterChange = useCallback((filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value || [] }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      category: [],
      section: [],
      bidArea: [],
      vendor: [],
      uom: [],
      size: [],
    });
    setSearchInput("");
    setDebouncedSearch("");
    setBidAreaFilter([]);
    setShowFilter(null);
  }, []);

  const hasActiveFilters =
    filters.category.length > 0 ||
    filters.section.length > 0 ||
    filters.bidArea.length > 0 ||
    filters.vendor.length > 0 ||
    filters.uom.length > 0 ||
    filters.size.length > 0 ||
    debouncedSearch ||
    bidAreaFilter.length > 0;

  const toggleFilter = (field) => {
    setShowFilter((prev) => (prev === field ? null : field));
  };

  // ── Assembly pagination ──
  const getAssemblyPage = (name) => assemblyPages[name] || 1;
  const setAssemblyPage = (name, page) => {
    setAssemblyPages((prev) => ({ ...prev, [name]: page }));
  };

  // ── Variance breakdown renderer ──
  const renderVarianceBreakdown = (item) => {
    const entries = item.changeOrderBreakdown || item.varianceBreakdown || [];
    if (entries.length === 0) {
      if (item.hasVariance) {
        return (
          <tr className="bg-gray-900/70 border-t border-amber-900/30">
            <td colSpan={14} className="p-3">
              <p className="text-xs text-gray-500 italic">
                Variance applied but no detailed breakdown available.
              </p>
            </td>
          </tr>
        );
      }
      return null;
    }

    // Group by budgetId
    const groups = {};
    entries.forEach((entry) => {
      const budgetIdStr = entry.budgetId?.toString() || "unknown";
      if (!groups[budgetIdStr]) {
        groups[budgetIdStr] = {
          budgetId: entry.budgetId,
          budgetTitle: entry.budgetTitle || "Variance",
          budgetType: entry.budgetType || "variance",
          reason: entry.reason || "",
          date: entry.date,
          entries: [],
        };
      }
      groups[budgetIdStr].entries.push(entry);
    });

    return (
      <tr className="bg-gray-900/70 border-t border-amber-900/30">
        <td colSpan={14} className="p-3">
          <div className="space-y-2">
            <h6 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Change & Variance Log
            </h6>
            {Object.values(groups).map((group, gIdx) => {
              const totalDeltaQty = group.entries.reduce(
                (sum, e) => sum + (e.deltaQty || 0),
                0,
              );
              const totalDeltaAmount = group.entries.reduce(
                (sum, e) => sum + (e.deltaAmount || 0),
                0,
              );

              return (
                <div
                  key={group.budgetId || gIdx}
                  className={`p-2.5 rounded-lg border ${
                    totalDeltaAmount > 0
                      ? "border-red-800/40 bg-red-950/20"
                      : totalDeltaAmount < 0
                        ? "border-emerald-800/40 bg-emerald-950/20"
                        : "border-gray-700 bg-gray-800/50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${
                          group.budgetType === "change_order"
                            ? "bg-purple-600/80 text-purple-100"
                            : "bg-amber-600/80 text-amber-100"
                        }`}
                      >
                        {group.budgetType === "change_order"
                          ? "CHANGE ORDER"
                          : "VARIANCE"}
                      </span>
                      <span className="text-xs font-medium text-white">
                        {group.budgetTitle}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-medium">
                      {totalDeltaQty !== 0 && (
                        <span
                          className={
                            totalDeltaQty > 0
                              ? "text-red-400"
                              : "text-emerald-400"
                          }
                        >
                          Qty: {totalDeltaQty > 0 ? "+" : ""}
                          {totalDeltaQty}
                        </span>
                      )}
                      {totalDeltaAmount !== 0 && (
                        <span
                          className={
                            totalDeltaAmount > 0
                              ? "text-red-400"
                              : "text-emerald-400"
                          }
                        >
                          {totalDeltaAmount > 0 ? "+" : ""}$
                          {Math.abs(totalDeltaAmount).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  {group.reason && (
                    <div className="mb-1.5 pl-2 border-l-2 border-amber-600/70 bg-amber-950/20 p-1.5 rounded-r">
                      <p className="text-[11px] text-amber-200">
                        <span className="font-medium text-amber-400">
                          Reason:{" "}
                        </span>
                        {group.reason}
                      </p>
                    </div>
                  )}

                  <div className="space-y-0.5 mt-1.5">
                    {group.entries.map((entry, eIdx) => {
                      const changeLabel =
                        entry.changeType === "price_change_new"
                          ? "Price Change"
                          : entry.changeType === "size_change_new"
                            ? "Size Change (New)"
                            : entry.changeType === "size_change_closeout"
                              ? "Size Closeout"
                              : entry.changeType === "qty_reduction"
                                ? "Qty Reduction"
                                : entry.changeType === "qty_increase"
                                  ? "Qty Increase"
                                  : entry.changeType === "qty_overage"
                                    ? "Qty Overage"
                                    : entry.changeType || "Qty Change";

                      return (
                        <div
                          key={entry.deltaItemId || eIdx}
                          className="flex items-center justify-between text-[11px] bg-gray-800/60 rounded px-2 py-1"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 font-mono w-5">
                              {entry.category || item.category}
                            </span>
                            <span className="text-gray-300">{changeLabel}</span>
                            {entry.sizeChangedFrom && (
                              <span className="text-gray-500">
                                {entry.sizeChangedFrom} → {entry.size}
                              </span>
                            )}
                            {entry.priceChangedFrom != null &&
                              entry.priceChangedTo != null && (
                                <span className="text-gray-500">
                                  ${formatNumber(entry.priceChangedFrom)} → $
                                  {formatNumber(entry.priceChangedTo)}
                                </span>
                              )}
                          </div>
                          <div className="flex items-center gap-3">
                            {entry.deltaQty !== 0 && (
                              <span
                                className={`font-mono ${
                                  entry.deltaQty > 0
                                    ? "text-red-400"
                                    : "text-emerald-400"
                                }`}
                              >
                                {entry.deltaQty > 0 ? "+" : ""}
                                {entry.deltaQty}
                              </span>
                            )}
                            {entry.deltaAmount !== 0 && (
                              <span
                                className={`font-mono ${
                                  entry.deltaAmount > 0
                                    ? "text-red-400"
                                    : "text-emerald-400"
                                }`}
                              >
                                {entry.deltaAmount > 0 ? "+" : ""}$
                                {Math.abs(entry.deltaAmount).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </span>
                            )}
                            {entry.date && (
                              <span className="text-gray-600 text-[10px]">
                                {new Date(entry.date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </td>
      </tr>
    );
  };

  // ── Filter dropdown component ──
  const FilterDropdown = ({ field, label, width = "180px" }) => {
    const isActive = filters[field]?.length > 0;
    return (
      <th
        className={`px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
          isActive ? "text-orange-400" : "text-gray-400"
        }`}
        onClick={() => toggleFilter(field)}
      >
        <span className="flex items-center gap-0.5">
          {label}
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
          {isActive && (
            <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
              {filters[field].length}
            </span>
          )}
        </span>
        {showFilter === field && (
          <div
            ref={filterRefs[field]}
            className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
            style={{ minWidth: width, width: "max-content", maxWidth: "350px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              <Select
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={getFilterOptions(field)}
                value={getFilterOptions(field).filter((opt) =>
                  filters[field]?.includes(opt.value),
                )}
                onChange={(options) =>
                  handleFilterChange(
                    field,
                    options ? options.map((o) => o.value) : [],
                  )
                }
                placeholder={`Select ${label.toLowerCase()}...`}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={multiSelectFilterStyles}
              />
            </div>
          </div>
        )}
      </th>
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-gray-400 text-sm">
          Loading assembly data...
        </span>
      </div>
    );
  }

  if (!allItems || allItems.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-sm">
          No confirmed budget items to display.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ── TOOLBAR ── */}
      <div className="bg-gray-800 rounded-lg p-3 space-y-2.5">
        {/* Row 1: Search + Source + Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px] max-w-[400px]">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Multi-token search (e.g. 358 drywall SF)..."
              className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput("");
                  setDebouncedSearch("");
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Bid Area Filter */}
          <div className="min-w-[200px] max-w-[320px]">
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={bidAreaOptions}
              value={bidAreaOptions.filter((opt) =>
                bidAreaFilter.includes(opt.value),
              )}
              onChange={(options) =>
                setBidAreaFilter(options ? options.map((o) => o.value) : [])
              }
              placeholder="Filter by Bid Area..."
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={multiSelectFilterStyles}
            />
          </div>

          {/* Expand/Collapse */}
          <div className="flex items-center gap-1">
            <button
              onClick={expandAll}
              className="px-2 py-1 text-[11px] text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-2 py-1 text-[11px] text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Collapse All
            </button>
          </div>

          {/* Variance button */}
          {selectedLineItems?.length > 0 && onCreateVariances && (
            <button
              onClick={onCreateVariances}
              className="px-3 py-1.5 text-[11px] font-medium text-white bg-orange-500 hover:bg-orange-600 rounded transition-colors"
            >
              + Variance ({selectedLineItems.length})
            </button>
          )}
        </div>

        {/* Row 2: Summary Stats + Active Filter Pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-400">
              <span className="text-white font-semibold">
                {grandTotals.assemblies}
              </span>{" "}
              assemblies
            </span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-400">
              <span className="text-white font-semibold">
                {grandTotals.items.toLocaleString()}
              </span>{" "}
              items
            </span>
            <span className="text-gray-600">|</span>
            <span className="text-green-400 font-medium">
              ${formatNumber(grandTotals.total)}
            </span>
            <span className="text-gray-600">|</span>
            <span className="text-blue-400">
              M: ${formatNumber(grandTotals.materials)}
            </span>
            <span className="text-orange-400">
              L: ${formatNumber(grandTotals.labor)}
            </span>
          </div>

          {/* Active filter pills */}
          {hasActiveFilters && (
            <>
              <span className="text-gray-600">|</span>
              {bidAreaFilter.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                  Area:{" "}
                  {bidAreaFilter.length === 1
                    ? bidAreaFilter[0]
                    : `${bidAreaFilter.length} selected`}
                  <button
                    onClick={() => setBidAreaFilter([])}
                    className="hover:text-teal-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.category.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-blue-600/80 text-white rounded">
                  {filters.category.join(", ")}
                  <button
                    onClick={() => handleFilterChange("category", [])}
                    className="hover:text-blue-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.section.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                  Sec:{" "}
                  {filters.section.length === 1
                    ? filters.section[0]
                    : `${filters.section.length} sel`}
                  <button
                    onClick={() => handleFilterChange("section", [])}
                    className="hover:text-purple-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.bidArea.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                  Area:{" "}
                  {filters.bidArea.length === 1
                    ? filters.bidArea[0]
                    : `${filters.bidArea.length} sel`}
                  <button
                    onClick={() => handleFilterChange("bidArea", [])}
                    className="hover:text-green-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.vendor.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-yellow-600/80 text-white rounded">
                  Vendor:{" "}
                  {filters.vendor.length === 1
                    ? vendorMap[filters.vendor[0]] || "Unassigned"
                    : `${filters.vendor.length} sel`}
                  <button
                    onClick={() => handleFilterChange("vendor", [])}
                    className="hover:text-yellow-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.uom.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                  UOM:{" "}
                  {filters.uom.length === 1
                    ? filters.uom[0]
                    : `${filters.uom.length} sel`}
                  <button
                    onClick={() => handleFilterChange("uom", [])}
                    className="hover:text-indigo-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.size.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-pink-600/80 text-white rounded">
                  Size:{" "}
                  {filters.size.length === 1
                    ? filters.size[0]
                    : `${filters.size.length} sel`}
                  <button
                    onClick={() => handleFilterChange("size", [])}
                    className="hover:text-pink-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {debouncedSearch && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-gray-600/80 text-white rounded">
                  "{debouncedSearch}"
                  <button
                    onClick={() => {
                      setSearchInput("");
                      setDebouncedSearch("");
                    }}
                    className="hover:text-gray-200"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
              >
                Clear all
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── ASSEMBLY CARDS ── */}
      <div className="space-y-1.5">
        {assemblyGroups.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              No assemblies match current filters.
            </p>
          </div>
        )}

        {assemblyGroups.map((group) => {
          const isExpanded = expandedAssemblies.has(group.assemblyName);
          const currentPage = getAssemblyPage(group.assemblyName);
          const totalGroupPages = Math.ceil(
            group.items.length / ITEMS_PER_ASSEMBLY_PAGE,
          );
          const paginatedGroupItems = group.items.slice(
            (currentPage - 1) * ITEMS_PER_ASSEMBLY_PAGE,
            currentPage * ITEMS_PER_ASSEMBLY_PAGE,
          );

          return (
            <div
              key={group.assemblyName}
              className="border border-gray-600 rounded-lg overflow-hidden shadow-lg bg-gray-700"
            >
              {/* Assembly Header */}
              <button
                onClick={() => toggleAssembly(group.assemblyName)}
                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* Expand chevron */}
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                      isExpanded ? "rotate-90" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>

                  {/* Assembly number + name */}
                  {group.assemblyNo && (
                    <span className="rounded bg-orange-500/15 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-orange-300">
                      #{group.assemblyNo}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-white">
                    {group.assemblyName}
                  </span>

                  {/* Item count badge */}
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-700 text-gray-300 rounded">
                    {group.itemCount} items
                  </span>

                  {/* Indicator badges */}
                  {group.hasChangeOrder && (
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-purple-600/60 text-purple-200 rounded">
                      CO
                    </span>
                  )}
                  {group.hasVariance && (
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-amber-600/60 text-amber-200 rounded">
                      VAR
                    </span>
                  )}
                </div>

                {/* Totals */}
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-blue-400">
                    M: ${formatNumber(group.materialAmount)}
                  </span>
                  <span className="text-orange-400">
                    L: ${formatNumber(group.laborAmount)}
                  </span>

                  {/* CO breakdown summary */}
                  {group.coBreakdownCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-purple-900/30 border border-purple-700/40">
                      <span className="text-[9px] font-semibold text-purple-300">
                        CO
                      </span>
                      {group.coTotalDeltaQty !== 0 && (
                        <span
                          className={`text-[11px] font-mono ${group.coTotalDeltaQty > 0 ? "text-red-400" : "text-emerald-400"}`}
                        >
                          {group.coTotalDeltaQty > 0 ? "+" : ""}
                          {group.coTotalDeltaQty} qty
                        </span>
                      )}
                      {group.coTotalDeltaAmount !== 0 && (
                        <span
                          className={`text-[11px] font-mono ${group.coTotalDeltaAmount > 0 ? "text-red-400" : "text-emerald-400"}`}
                        >
                          {group.coTotalDeltaAmount > 0 ? "+" : ""}$
                          {formatNumber(Math.abs(group.coTotalDeltaAmount))}
                        </span>
                      )}
                    </span>
                  )}

                  {/* Variance breakdown summary */}
                  {group.varianceBreakdownCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-900/30 border border-amber-700/40">
                      <span className="text-[9px] font-semibold text-amber-300">
                        VAR
                      </span>
                      {group.varianceTotalDeltaQty !== 0 && (
                        <span
                          className={`text-[11px] font-mono ${group.varianceTotalDeltaQty > 0 ? "text-red-400" : "text-emerald-400"}`}
                        >
                          {group.varianceTotalDeltaQty > 0 ? "+" : ""}
                          {group.varianceTotalDeltaQty} qty
                        </span>
                      )}
                      {group.varianceTotalDeltaAmount !== 0 && (
                        <span
                          className={`text-[11px] font-mono ${group.varianceTotalDeltaAmount > 0 ? "text-red-400" : "text-emerald-400"}`}
                        >
                          {group.varianceTotalDeltaAmount > 0 ? "+" : ""}$
                          {formatNumber(
                            Math.abs(group.varianceTotalDeltaAmount),
                          )}
                        </span>
                      )}
                    </span>
                  )}

                  <span className="text-green-400 font-semibold text-sm">
                    ${formatNumber(group.totalAmount)}
                  </span>
                </div>
              </button>

              {/* Expanded Items Table */}
              {isExpanded && (
                <div className="border-t border-gray-700/60">
                  <div className="overflow-x-auto rounded-lg border border-gray-700 px-2 isolate">
                    <table className="w-full text-[13px] mb-12">
                      <thead className="bg-gray-800/95 sticky top-0 z-10">
                        <tr className="border-b border-gray-700">
                          <th className="w-8 px-1 py-1.5 text-center text-[13px] font-medium text-gray-400">
                            <input
                              type="checkbox"
                              checked={
                                group.items.length > 0 &&
                                group.items.every((item) =>
                                  selectedLineItems?.some(
                                    (s) =>
                                      getItemSelectionKey?.(s) ===
                                      getItemSelectionKey?.(item),
                                  ),
                                )
                              }
                              onChange={() => {
                                if (!onSelectAllFiltered) return;
                                const allSelected = group.items.every((item) =>
                                  selectedLineItems?.some(
                                    (s) =>
                                      getItemSelectionKey?.(s) ===
                                      getItemSelectionKey?.(item),
                                  ),
                                );
                                group.items.forEach((item) => {
                                  const isSelected = selectedLineItems?.some(
                                    (s) =>
                                      getItemSelectionKey?.(s) ===
                                      getItemSelectionKey?.(item),
                                  );
                                  if (allSelected && isSelected) {
                                    onSelectLineItem?.(item);
                                  } else if (!allSelected && !isSelected) {
                                    onSelectLineItem?.(item);
                                  }
                                });
                              }}
                              className="h-3 w-3 rounded border-gray-500 text-orange-500"
                            />
                          </th>
                          <th className="w-12 px-1 py-1.5 text-left text-[13px] font-medium text-gray-400">
                            #
                          </th>
                          <th className="w-12 px-1 py-1.5 text-center text-[13px] font-medium text-gray-400">
                            Src
                          </th>
                          <FilterDropdown
                            field="section"
                            label="Section"
                            width="180px"
                          />
                          <FilterDropdown
                            field="category"
                            label="Cat"
                            width="140px"
                          />
                          <th className="min-w-[180px] px-1 py-1.5 text-left text-[13px] font-medium text-gray-400">
                            Description
                          </th>
                          <FilterDropdown
                            field="size"
                            label="Size"
                            width="160px"
                          />
                          <th className="w-16 px-1 py-1.5 text-right text-[13px] font-medium text-gray-400">
                            Qty
                          </th>
                          <FilterDropdown
                            field="uom"
                            label="UOM"
                            width="160px"
                          />
                          <th className="w-28 px-1 py-1.5 text-right text-[13px] font-medium text-gray-400">
                            Cost/Rate
                          </th>
                          <th className="w-28 px-1 py-1.5 text-right text-[13px] font-medium text-gray-400">
                            Subtotal
                          </th>
                          <FilterDropdown
                            field="bidArea"
                            label="Bid Area"
                            width="280px"
                          />
                          <FilterDropdown
                            field="vendor"
                            label="Vendor"
                            width="240px"
                          />
                          <th className="w-32 px-2 py-1.5 text-left text-[13px] font-medium text-gray-400">
                            PO / WO #
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/50">
                        {paginatedGroupItems.map((item, index) => {
                          const displayQty = getDisplayQty(item);
                          const displayPrice = getDisplayPrice(item);
                          const vendorId = item._vendorId;
                          const isSelected = selectedLineItems?.some(
                            (s) =>
                              getItemSelectionKey?.(s) ===
                              getItemSelectionKey?.(item),
                          );

                          return (
                            <React.Fragment
                              key={item._id || item.key || `item-${index}`}
                            >
                              <tr
                                className={`hover:bg-gray-700/50 transition-colors cursor-pointer ${
                                  index % 2 === 0
                                    ? "bg-gray-800/50"
                                    : "bg-gray-400/10"
                                } ${
                                  isSelected
                                    ? "ring-1 ring-orange-500/50 bg-orange-900/20"
                                    : ""
                                } ${item.isHistoricalPriceLine ? "bg-gray-800/70 border-l-4 border-gray-500" : ""}
                        ${item.isPriceChangeRow && !item.isHistoricalPriceLine ? "bg-purple-900/15 border-l-4 border-purple-500" : ""}
                        ${item.closedOutBySizeChange ? "bg-yellow-900/10" : ""}`}
                                onClick={() =>
                                  setExpandedItemId(
                                    expandedItemId === (item._id || item.key)
                                      ? null
                                      : item._id || item.key,
                                  )
                                }
                              >
                                {/* Checkbox */}
                                <td className="w-8 px-1 py-1 text-center">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => onSelectLineItem?.(item)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-3 w-3 text-orange-500 focus:ring-orange-500 border-gray-500 rounded"
                                  />
                                </td>
                                {/* Item number */}
                                <td className="w-12 px-1 py-1 text-[13px] text-gray-400 font-mono">
                                  {item.itemNumberDisplay
                                    ?.toFixed(2)
                                    .replace(/\.00$/, "") ||
                                    item.itemNumber ||
                                    "—"}
                                </td>
                                {/* Source badge */}
                                <td className="w-12 px-1 py-1 text-center">
                                  <SourceBadge item={item} />
                                </td>
                                {/* Section */}
                                <td
                                  className="w-20 px-1 py-1 text-[13px] text-gray-300 truncate"
                                  title={item.sectionNumber}
                                >
                                  {item.sectionNumber || "—"}
                                </td>
                                {/* Category */}
                                <td className="w-10 px-1 py-1 text-center">
                                  <span
                                    className={`inline-block px-1.5 py-0.5 text-[12px] font-semibold rounded ${
                                      item.category === "M"
                                        ? "bg-blue-900/50 text-blue-300"
                                        : "bg-orange-900/50 text-orange-300"
                                    }`}
                                  >
                                    {item.category}
                                  </span>
                                </td>
                                {/* Description */}
                                <td
                                  className="w-48 px-1 py-1 text-[13px] text-gray-200"
                                  title={
                                    item.itemDescription ||
                                    item.costCodeDescription
                                  }
                                >
                                  <div className="max-w-[250px] break-words">
                                    {item.itemDescription ||
                                      item.costCodeDescription ||
                                      "—"}
                                  </div>
                                  <ItemStatusBadges item={item} />
                                  {item.note && (
                                    <span className="text-[10px] text-amber-400 italic block mt-0.5">
                                      {item.note}
                                    </span>
                                  )}
                                </td>
                                {/* Size */}
                                <td className="w-24 px-1 py-1 text-[13px] text-gray-400">
                                  <div className="flex flex-col">
                                    <span>
                                      {item.size || item.latestSize || "—"}
                                    </span>
                                    {item.isNewSpec && item.sizeChangedFrom && (
                                      <span className="text-[10px] text-blue-400">
                                        was: {item.sizeChangedFrom}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {/* Qty */}
                                <td className="px-2 py-1 text-[13px] text-right">
                                  <div className="flex flex-col items-end">
                                    <span
                                      className={`font-mono font-semibold ${
                                        item.isHistoricalPriceLine
                                          ? "text-gray-400"
                                          : "text-gray-200"
                                      }`}
                                    >
                                      {displayQty}
                                    </span>
                                    {item.baseRequiredQty !== undefined &&
                                      item.baseRequiredQty !== displayQty &&
                                      !item.isHistoricalPriceLine && (
                                        <span className="text-xs text-gray-500">
                                          base: {item.baseRequiredQty}
                                        </span>
                                      )}
                                    {item.coTotalDeltaQty !== undefined &&
                                      item.coTotalDeltaQty !== 0 &&
                                      !item.isHistoricalPriceLine && (
                                        <span
                                          className={`text-xs ${
                                            item.coTotalDeltaQty > 0
                                              ? "text-red-400"
                                              : "text-green-400"
                                          }`}
                                        >
                                          {item.coTotalDeltaQty > 0 ? "+" : ""}
                                          {item.coTotalDeltaQty} Δ
                                        </span>
                                      )}
                                  </div>
                                </td>
                                {/* UOM */}
                                <td
                                  className="w-14 px-1 py-1 text-[13px] text-center"
                                  title={item.uom || item.container}
                                >
                                  <span
                                    className={`inline-block px-1.5 py-0.5 text-[13px] font-medium rounded ${
                                      getDisplayUom(item) === "PC"
                                        ? "bg-teal-900/50 text-teal-300"
                                        : "bg-gray-700/60 text-gray-400"
                                    }`}
                                  >
                                    {getDisplayUom(item)}
                                  </span>
                                </td>
                                {/* Cost/Rate */}

                                <td className="w-28 px-1 py-1 text-[13px] text-right">
                                  <div className="flex flex-col items-end gap-0.5">
                                    <span
                                      className={`font-mono ${item.isHistoricalPriceLine ? "text-gray-400" : item.isPriceChangeRow ? "text-purple-300" : "text-gray-200"}`}
                                    >
                                      $
                                      {formatNumber(
                                        getDisplayPackageCost(item),
                                      )}
                                    </span>
                                    {(() => {
                                      const uom = (
                                        item.uom ||
                                        item.container ||
                                        ""
                                      )
                                        .trim()
                                        .toUpperCase();
                                      const isCoverageBased = ["SF", "LF"].some(
                                        (u) => uom.includes(u),
                                      );
                                      return isCoverageBased &&
                                        item.category === "M" ? (
                                        <span className="text-[13px] text-gray-500">
                                          ${formatNumber(displayPrice)}/{uom}
                                        </span>
                                      ) : null;
                                    })()}
                                    {item.priceChangedFrom &&
                                      !item.isHistoricalPriceLine &&
                                      item.priceChangedFrom !==
                                        displayPrice && (
                                        <span className="text-[13px] text-gray-500">
                                          was $
                                          {formatNumber(item.priceChangedFrom)}
                                        </span>
                                      )}
                                  </div>
                                </td>
                                {/* Subtotal */}
                                <td className="w-28 px-1 py-1 text-[13px] text-right font-medium">
                                  <div className="flex flex-col items-end">
                                    <span
                                      className={`font-mono ${
                                        item.isHistoricalPriceLine
                                          ? "text-gray-400"
                                          : item.category === "M"
                                            ? "text-blue-300"
                                            : "text-orange-300"
                                      }`}
                                    >
                                      ${formatNumber(item.amount || 0)}
                                    </span>
                                    {item.originalAmount != null &&
                                      Math.abs(
                                        item.originalAmount -
                                          (item.amount || 0),
                                      ) > 1 &&
                                      !item.isHistoricalPriceLine && (
                                        <span className="text-xs text-gray-500">
                                          was $
                                          {formatNumber(item.originalAmount)}
                                        </span>
                                      )}
                                  </div>
                                </td>
                                {/* Bid Area */}
                                <td
                                  className="w-32 px-1 py-1 text-[13px] text-gray-400 truncate"
                                  title={item.bidArea}
                                >
                                  {item.bidArea || "—"}
                                </td>
                                {/* Vendor */}
                                <td className="w-40 px-1 py-1 text-[13px] text-gray-400 truncate">
                                  {item.category === "M"
                                    ? vendorMap[vendorId] || "—"
                                    : "—"}
                                </td>
                                {/* PO / WO */}
                                <td className="w-40 px-3 py-2 text-[13px]">
                                  {item.category === "M" && item.linkedPO ? (
                                    (() => {
                                      const po = project.purchaseOrders2?.find(
                                        (p) =>
                                          String(p._id) ===
                                          String(item.linkedPO),
                                      );
                                      return po?.poNumber ? (
                                        <span className="inline-flex items-center gap-1.5 text-blue-300 font-medium">
                                          <span className="text-xs text-blue-400">
                                            PO -
                                          </span>
                                          {po.poNumber}
                                        </span>
                                      ) : (
                                        <span className="text-amber-400">
                                          PO (missing)
                                        </span>
                                      );
                                    })()
                                  ) : item.category === "L" && item.linkedWO ? (
                                    (() => {
                                      const wo = project.workOrders2?.find(
                                        (w) =>
                                          String(w._id) ===
                                          String(item.linkedWO),
                                      );
                                      return wo?.woNumber ? (
                                        <span className="inline-flex items-center gap-1.5 text-purple-300 font-medium">
                                          {wo.woNumber}
                                        </span>
                                      ) : (
                                        <span className="text-amber-400">
                                          WO (missing)
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-gray-500">—</span>
                                  )}
                                </td>
                              </tr>

                              {/* Expanded variance breakdown */}
                              {expandedItemId === (item._id || item.key) &&
                                renderVarianceBreakdown(item)}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination for large assemblies */}
                  {totalGroupPages > 1 && (
                    <div className="flex items-center justify-center gap-2 py-4 border-t border-gray-700/40">
                      <button
                        onClick={() =>
                          setAssemblyPage(
                            group.assemblyName,
                            Math.max(1, currentPage - 1),
                          )
                        }
                        disabled={currentPage === 1}
                        className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
                      >
                        ‹ Prev
                      </button>
                      <span className="text-[13px] text-white">
                        Page {currentPage} of {totalGroupPages}
                      </span>
                      <button
                        onClick={() =>
                          setAssemblyPage(
                            group.assemblyName,
                            Math.min(totalGroupPages, currentPage + 1),
                          )
                        }
                        disabled={currentPage === totalGroupPages}
                        className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
                      >
                        Next ›
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
